import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leadsApi, sourcesApi, LeadFilters, Lead } from '../api/client';

// Query keys
export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (filters: LeadFilters) => [...leadKeys.lists(), filters] as const,
  details: () => [...leadKeys.all, 'detail'] as const,
  detail: (id: number) => [...leadKeys.details(), id] as const,
  stats: () => [...leadKeys.all, 'stats'] as const,
  activities: (id: number) => [...leadKeys.all, 'activities', id] as const,
};

export const sourceKeys = {
  all: ['sources'] as const,
  lists: () => [...sourceKeys.all, 'list'] as const,
  detail: (id: number) => [...sourceKeys.all, 'detail', id] as const,
  runs: (id: number) => [...sourceKeys.all, 'runs', id] as const,
};

// Lead hooks
export function useLeads(filters: LeadFilters = {}) {
  return useQuery({
    queryKey: leadKeys.list(filters),
    queryFn: () => leadsApi.getLeads(filters),
  });
}

export function useLead(id: number) {
  return useQuery({
    queryKey: leadKeys.detail(id),
    queryFn: () => leadsApi.getLead(id),
    enabled: id > 0,
  });
}

export function useLeadStats() {
  return useQuery({
    queryKey: leadKeys.stats(),
    queryFn: () => leadsApi.getStats(),
  });
}

export function useLeadActivities(id: number) {
  return useQuery({
    queryKey: leadKeys.activities(id),
    queryFn: () => leadsApi.getActivities(id),
    enabled: id > 0,
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Lead> }) =>
      leadsApi.updateLead(id, data),
    onSuccess: (updatedLead) => {
      // Update the specific lead in cache
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.invalidateQueries({ queryKey: leadKeys.stats() });
    },
  });
}

export function useAssignLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, assignedTo }: { id: number; assignedTo: string }) =>
      leadsApi.assignLead(id, assignedTo),
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

// Source hooks
export function useSources() {
  return useQuery({
    queryKey: sourceKeys.lists(),
    queryFn: () => sourcesApi.getSources(),
  });
}

export function useSourceRuns(id: number) {
  return useQuery({
    queryKey: sourceKeys.runs(id),
    queryFn: () => sourcesApi.getRuns(id),
    enabled: id > 0,
  });
}

export function useTriggerFetch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: number) => sourcesApi.triggerFetch(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: sourceKeys.all });
    },
  });
}

export function useTriggerFetchAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => sourcesApi.triggerFetchAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: sourceKeys.all });
    },
  });
}

export function useClaimLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => leadsApi.claimLead(id),
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}

export function useReleaseLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => leadsApi.releaseLead(id),
    onSuccess: (updatedLead) => {
      queryClient.setQueryData(leadKeys.detail(updatedLead.id), updatedLead);
      queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
    },
  });
}
